import { CircleAlert, CheckCircle2 } from "lucide-react";
import { Progress } from "../../ui/progress";

interface UploadProgressProps {
  readonly status: "idle" | "pending" | "success" | "error";
  readonly progress: number;
  readonly transport: "resumable" | "multipart" | null;
}

class UnexpectedUploadStateError extends Error {
  readonly name = "UnexpectedUploadStateError";

  constructor(readonly state: never) {
    super(`Unexpected upload state: ${String(state)}`);
  }
}

function assertNever(value: never): never {
  throw new UnexpectedUploadStateError(value);
}

function transportGuidance(
  transport: UploadProgressProps["transport"],
): string {
  switch (transport) {
    case "resumable":
      return "Si la conexión se interrumpe, retomaremos desde el último avance confirmado";
    case "multipart":
    case null:
      return "Mantén esta ventana abierta hasta terminar la carga";
    default:
      return assertNever(transport);
  }
}

export function UploadProgress({
  status,
  progress,
  transport,
}: UploadProgressProps) {
  const normalizedProgress = Math.min(100, Math.max(0, Math.round(progress)));

  switch (status) {
    case "idle":
      return null;
    case "success":
      return (
        <div
          role="status"
          className="flex items-center gap-2 text-sm font-medium text-emerald-700"
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          Carga completa
        </div>
      );
    case "error":
      return (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm font-medium text-red-700"
        >
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          No se pudo completar la carga. Intenta nuevamente.
        </div>
      );
    case "pending":
      break;
    default:
      return assertNever(status);
  }

  return (
    <div className="space-y-2" aria-live="polite">
      <div
        data-testid="upload-progress-labels"
        className="flex flex-col gap-1 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>
          {normalizedProgress >= 100
            ? "Confirmando carga…"
            : "Subiendo archivos…"}{" "}
          {normalizedProgress}%
        </span>
        <span>{transportGuidance(transport)}</span>
      </div>
      <Progress
        value={normalizedProgress}
        aria-label="Progreso de carga"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={normalizedProgress}
      />
    </div>
  );
}
